import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
} from "lucide-react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import girlsDream from "./assets/images/partners_clients/girls_dream.png";
import logoMystik from "./assets/images/partners_clients/logo mystik.png";
import vercelLogo from "./assets/images/partners_clients/vercel.png";

// Intersection Observer Hook
const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const FlowKraftWebsite = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [formStatus, setFormStatus] = useState("");
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    const sliderEl = sliderRef.current;
    if (!sectionEl || !sliderEl) return;

    const handleWheel = (e) => {
      if (e.deltaY === 0) return;

      const scrollLeft = Math.ceil(sliderEl.scrollLeft);
      const maxScrollLeft = sliderEl.scrollWidth - sliderEl.clientWidth;
      
      const canScrollLeft = scrollLeft > 2;
      const canScrollRight = scrollLeft < maxScrollLeft - 2;

      if ((e.deltaY > 0 && canScrollRight) || (e.deltaY < 0 && canScrollLeft)) {
        e.preventDefault();
        sliderEl.scrollLeft += e.deltaY;
      }
    };

    sectionEl.addEventListener("wheel", handleWheel, { passive: false });
    return () => sectionEl.removeEventListener("wheel", handleWheel);
  }, []);

  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");

    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 3000);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 3000);
      return;
    }

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_name: "FlowKraft Agency",
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setFormStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setFormStatus(""), 5000);
    } catch (error) {
      setFormStatus("error");
      setTimeout(() => setFormStatus(""), 3000);
    }
  };

  const services = [
    {
      num: "01",
      title: "Développement Web",
      slug: "developpement-web",
      description: "Applications web sur mesure avec React, Vue.js et les technologies les plus performantes.",
      image: "/images/dev-web/2150170149.jpg",
    },
    {
      num: "02",
      title: "Développement Mobile",
      slug: "developpement-mobile",
      description: "Applications natives et cross-platform performantes pour iOS et Android.",
      image: "/images/dev-mobile/2150104509.jpg",
    },
    {
      num: "03",
      title: "Design UX/UI",
      slug: "design-ux-ui",
      description: "Expériences utilisateur mémorables et interfaces qui convertissent.",
      image: "/images/design-ui/2149930986.jpg",
    },
    {
      num: "04",
      title: "Marketing Digital",
      slug: "marketing-digital",
      description: "Stratégies digitales complètes pour maximiser votre visibilité.",
      image: "/images/marketing-digital/167088.jpg",
    },
    {
      num: "05",
      title: "Consulting",
      slug: "consulting",
      description: "Accompagnement stratégique pour votre transformation digitale.",
      image: "/images/consulting/1503.jpg",
    },
  ];

  const projects = [
    {
      title: "SalonHub",
      category: "SaaS",
      description: "Plateforme de réservation pour salons de coiffure",
      image: "/images/dev-web/1.png",
      link: "https://salonhub.flowkraftagency.com",
    },
    {
      title: "Perform-Co",
      category: "Site Web",
      description: "Plateforme d'accompagnement à la performance",
      image: "/images/portfolio/perform-co.png",
      link: "https://perform-co.com/",
    },
    {
      title: "OmniBat",
      category: "Site Web",
      description: "Solution de gestion pour le bâtiment",
      image: "/images/portfolio/omnibat.png",
      link: "https://omnibat.pro/",
    },
    {
      title: "TeamShuffle",
      category: "Mobile App",
      description: "Organisation de tournois sportifs",
      image: "/images/dev-mobile/miniature.jpeg",
      link: "https://teamshuffle.flowkraftagency.com",
    }
    
  ];

  const processSteps = [
    { num: "01", title: "Découverte", desc: "Compréhension de vos besoins et objectifs" },
    { num: "02", title: "Stratégie", desc: "Définition du plan d'action optimal" },
    { num: "03", title: "Design", desc: "Création des maquettes et prototypes" },
    { num: "04", title: "Développement", desc: "Construction de votre solution" },
    { num: "05", title: "Lancement", desc: "Mise en production et support" },
  ];

  const testimonials = [
    {
      quote: "FlowKraft Agency m'a accompagné dans la refonte complète de mon identité digitale. Résultat : +47% de conversion en 30 jours.",
      author: "Roland GBETANOU",
      role: "Entrepreneur",
    },
    {
      quote: "Un travail d'une grande qualité ! Depuis la refonte, notre site web attire beaucoup plus de visiteurs.",
      author: "Gilles-Christ G.",
      role: "GCSGC Agency",
    },
    {
      quote: "J'étais très impressionnée par le travail en équipe qui a été fait ainsi que le rendu final.",
      author: "Flasolia",
      role: "Créatrice",
    },
  ];

  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <Header />

      {/* ============================================
          HERO SECTION - Split Layout with Image
          ============================================ */}
      <section className="min-h-screen relative">
        <div className="grid lg:grid-cols-2 min-h-screen">
          {/* Left: Content */}
          <div className="flex flex-col justify-center px-6 md:px-12 lg:px-20 py-32 lg:py-20 order-2 lg:order-1">
            <div className="max-w-xl">
              {/* Label */}
              <div
                className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-8 opacity-0 animate-slideUp"
                style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}
              >
                Agence Digitale
              </div>

              {/* Title */}
              <h1
                className="font-display text-hero text-white mb-6 opacity-0 animate-slideUp"
                style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}
              >
                Créons le
                <br />
                <span className="text-flowkraft-secondary italic">digital</span>
                <br />
                de demain
              </h1>

              {/* Subtitle */}
              <p
                className="text-lg text-gray-400 mb-10 max-w-md leading-relaxed opacity-0 animate-slideUp"
                style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}
              >
                Agence digitale spécialisée dans la conception et le développement
                de solutions web et mobile sur mesure.
              </p>

              {/* CTA */}
              <div
                className="flex flex-wrap gap-4 opacity-0 animate-slideUp"
                style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
              >
                <a href="#contact" className="btn-solid">
                  Démarrer un projet
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#services" className="btn-outline-minimal">
                  Découvrir
                </a>
              </div>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative order-1 lg:order-2 h-[50vh] lg:h-auto">
            <div
              className="absolute inset-0 opacity-0 animate-scaleReveal"
              style={{ animationDelay: "0.3s", animationFillMode: "forwards" }}
            >
              <img
                src="/images/consulting/1503.jpg"
                alt="Notre équipe"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/50 to-transparent lg:via-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent lg:hidden" />
            </div>

            {/* Floating Badge */}
            <div className="absolute bottom-8 right-8 bg-white/5 backdrop-blur-sm border border-white/10 px-6 py-4 hidden lg:block">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">Basé à</div>
              <div className="text-white font-medium">Toulouse, France</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SERVICES SECTION - Editorial List
          ============================================ */}
      <section id="services" className="py-24 md:py-32 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Section Header */}
          <SectionHeader
            label="Services"
            title="Ce que nous faisons"
            description="Des solutions digitales complètes pour transformer votre vision en réalité."
          />

          {/* Services List */}
          <div className="mt-16">
            {services.map((service, index) => (
              <ServiceItem key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PORTFOLIO SECTION - Editorial Grid
          ============================================ */}
      <section 
        id="portfolio" 
        ref={sectionRef} 
        className="py-24 md:py-32 bg-gray-900/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            label="Portfolio"
            title="Nos réalisations"
            description="Une sélection de projets qui illustrent notre expertise."
          />

          {/* Projects Slider - Horizontal Scroll */}
          <div 
            ref={sliderRef}
            className="mt-16 flex overflow-x-auto snap-x md:snap-none snap-mandatory gap-8 pb-12 no-scrollbar"
          >
            {projects.map((project, index) => (
              <div key={index} className="flex-shrink-0 w-[85vw] md:w-[600px] snap-center">
                <ProjectCard project={project} />
              </div>
            ))}
          </div>

          {/* View More CTA */}
          <div className="mt-12 text-center">
            <Link to="/portfolio" className="btn-outline-minimal inline-flex items-center gap-2">
              Voir toutes nos réalisations
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          PROCESS SECTION - Minimal Timeline
          ============================================ */}
      <section id="process" className="py-24 md:py-32 bg-gray-950 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-5">
          <img
            src="/images/main/121215.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <SectionHeader
            label="Méthodologie"
            title="Notre approche"
            description="Un processus structuré pour garantir le succès de chaque projet."
          />

          {/* Process Steps */}
          <div className="mt-16 grid md:grid-cols-5 gap-px bg-white/10">
            {processSteps.map((step, index) => (
              <ProcessStep key={index} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          TESTIMONIALS SECTION
          ============================================ */}
      <section id="testimonials" className="py-24 md:py-32 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            label="Témoignages"
            title="Ils nous font confiance"
          />

          {/* Testimonials Grid */}
          <div className="mt-16 grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index} testimonial={testimonial} index={index} />
            ))}
          </div>

          {/* Partners Marquee */}
          <div className="mt-24 pt-16 border-t border-white/10 overflow-hidden">
            <p className="text-center text-sm text-gray-500 mb-12 uppercase tracking-wider">
              Partenaires & Clients
            </p>
            
            <div className="relative overflow-hidden">
              <div className="animate-marquee flex items-center py-4 flex-shrink-0">
                {[
                  { name: "Amazon", image: "/images/partners/amazon.svg" },
                  { name: "DA", image: "/images/partners/da.svg" },
                  { name: "House", image: "/images/partners/house.svg" },
                  { name: "LWS", image: "/images/partners/lws.svg" },
                  { name: "Girls Dream", image: girlsDream },
                  { name: "Mystik", image: logoMystik },
                  { name: "Vercel", image: vercelLogo },
                ].map((partner, i) => (
                  <div key={i} className="mx-20 flex-shrink-0">
                    <img
                      src={partner.image}
                      alt={partner.name}
                      className="h-20 md:h-24 w-auto opacity-40 hover:opacity-100 transition-opacity duration-300"
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  </div>
                ))}
                {/* Duplicate for seamless loop */}
                {[
                  { name: "Amazon", image: "/images/partners/amazon.svg" },
                  { name: "DA", image: "/images/partners/da.svg" },
                  { name: "House", image: "/images/partners/house.svg" },
                  { name: "LWS", image: "/images/partners/lws.svg" },
                  { name: "Girls Dream", image: girlsDream },
                  { name: "Mystik", image: logoMystik },
                  { name: "Vercel", image: vercelLogo },
                ].map((partner, i) => (
                  <div key={`dup-${i}`} className="mx-20 flex-shrink-0">
                    <img
                      src={partner.image}
                      alt={partner.name}
                      className="h-20 md:h-24 w-auto opacity-40 hover:opacity-100 transition-opacity duration-300"
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          CONTACT SECTION
          ============================================ */}
      <section id="contact" className="py-24 md:py-32 bg-gray-950 relative">
        {/* Background */}
        <div className="absolute inset-0 opacity-10">
          <img
            src="/images/main/4256.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left: Info */}
            <div>
              <div className="inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6">
                Contact
              </div>
              <h2 className="font-display text-display text-white mb-6">
                Discutons de votre projet
              </h2>
              <p className="text-gray-400 text-lg mb-12 max-w-md">
                Prêt à donner vie à vos idées ? Contactez-nous pour une consultation gratuite.
              </p>

              {/* Contact Details */}
              <div className="space-y-6">
                <ContactDetail
                  icon={<MapPin className="w-5 h-5" />}
                  label="Adresse"
                  value="Toulouse, France"
                />
                <ContactDetail
                  icon={<Mail className="w-5 h-5" />}
                  label="Email"
                  value="info@flowkraftagency.com"
                  href="mailto:info@flowkraftagency.com"
                />
                <ContactDetail
                  icon={<Phone className="w-5 h-5" />}
                  label="Téléphone"
                  value="+33 6 52 79 45 62"
                  href="tel:+33652794562"
                />
              </div>

              {/* Social */}
              <div className="mt-12 pt-8 border-t border-white/10">
                <p className="text-sm text-gray-500 mb-4">Suivez-nous</p>
                <div className="flex gap-4">
                  {[
                    { name: "LinkedIn", href: "https://www.linkedin.com/company/flowkraft-agency/" },
                    { name: "Instagram", href: "https://www.instagram.com/flowkraft_agency" },
                    { name: "Facebook", href: "https://web.facebook.com/flowkraftagency" },
                    { name: "GitHub", href: "https://github.com/FlowKraft-Agency" },
                  ].map((social, i) => (
                    <a
                      key={i}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-flowkraft-secondary transition-colors text-sm"
                    >
                      {social.name}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div>
              <form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nom complet</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="input-clean"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="input-clean"
                    placeholder="jean@exemple.fr"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleFormChange}
                    rows={4}
                    required
                    className="input-clean resize-none"
                    placeholder="Décrivez votre projet..."
                  />
                </div>

                {/* Status */}
                {formStatus === "success" && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Check className="w-4 h-4" />
                    Message envoyé avec succès
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="text-red-400 text-sm">
                    Une erreur est survenue. Veuillez réessayer.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === "sending"}
                  className="btn-solid w-full justify-center disabled:opacity-50"
                >
                  {formStatus === "sending" ? "Envoi en cours..." : "Envoyer le message"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ============================================
   COMPONENTS
   ============================================ */

const SectionHeader = ({ label, title, description }) => {
  const [ref, isInView] = useInView();

  return (
    <div ref={ref} className="max-w-2xl">
      <div
        className={`inline-block px-4 py-1 border border-flowkraft-primary/30 text-flowkraft-secondary text-sm mb-6 opacity-0 ${isInView ? "animate-slideUp" : ""}`}
        style={{ animationFillMode: "forwards" }}
      >
        {label}
      </div>
      <h2
        className={`font-display text-display text-white mb-4 opacity-0 ${isInView ? "animate-slideUp delay-100" : ""}`}
        style={{ animationFillMode: "forwards" }}
      >
        {title}
      </h2>
      {description && (
        <p
          className={`text-gray-400 text-lg opacity-0 ${isInView ? "animate-slideUp delay-200" : ""}`}
          style={{ animationFillMode: "forwards" }}
        >
          {description}
        </p>
      )}
    </div>
  );
};

const ServiceItem = ({ service, index }) => {
  const [ref, isInView] = useInView();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      ref={ref}
      to={`/services/${service.slug}`}
      className={`group block border-b border-white/10 py-8 first:pt-0 last:border-b-0 opacity-0 ${isInView ? "animate-slideUp" : ""}`}
      style={{ animationDelay: `${index * 0.1}s`, animationFillMode: "forwards" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="grid md:grid-cols-12 gap-6 items-center">
        {/* Number */}
        <div className="md:col-span-1">
          <span className="font-display text-2xl text-flowkraft-primary/50 group-hover:text-flowkraft-secondary transition-colors">
            {service.num}
          </span>
        </div>

        {/* Title */}
        <div className="md:col-span-4">
          <h3 className="text-2xl md:text-3xl font-display text-white group-hover:text-flowkraft-secondary transition-colors">
            {service.title}
          </h3>
        </div>

        {/* Description */}
        <div className="md:col-span-5">
          <p className="text-gray-400 group-hover:text-gray-300 transition-colors">
            {service.description}
          </p>
        </div>

        {/* Arrow + Image Preview */}
        <div className="md:col-span-2 flex justify-end items-center gap-4">
          {/* Image Preview on Hover */}
          <div
            className={`hidden lg:block w-24 h-16 overflow-hidden transition-all duration-500 ${
              isHovered ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
            }`}
          >
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="w-12 h-12 border border-white/20 flex items-center justify-center group-hover:border-flowkraft-secondary group-hover:bg-flowkraft-secondary transition-all duration-300">
            <ArrowUpRight className="w-5 h-5 text-white/50 group-hover:text-white transition-colors" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const ProjectCard = ({ project }) => {
  const [ref, isInView] = useInView();

  return (
    <a
      ref={ref}
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className={`group block relative overflow-hidden opacity-0 ${isInView ? "animate-scaleReveal" : ""} aspect-video`}
      style={{ animationFillMode: "forwards" }}
    >
      {/* Image */}
      <img
        src={project.image}
        alt={project.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/20 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
        <span className="text-xs text-flowkraft-secondary uppercase tracking-wider mb-2">
          {project.category}
        </span>
        <h3 className="text-2xl md:text-3xl font-display text-white mb-2 group-hover:text-flowkraft-secondary transition-colors">
          {project.title}
        </h3>
        <p className="text-gray-400 text-sm max-w-xs">
          {project.description}
        </p>

        {/* View Arrow */}
        <div className="absolute top-6 right-6 w-10 h-10 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:bg-white">
          <ArrowUpRight className="w-4 h-4 text-white group-hover:text-gray-950" />
        </div>
      </div>
    </a>
  );
};

const ProcessStep = ({ step, index }) => {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`bg-gray-950 p-8 text-center opacity-0 ${isInView ? "animate-slideUp" : ""}`}
      style={{ animationDelay: `${index * 0.1}s`, animationFillMode: "forwards" }}
    >
      <div className="font-display text-5xl text-flowkraft-primary/20 mb-4">
        {step.num}
      </div>
      <h3 className="text-lg font-medium text-white mb-2">{step.title}</h3>
      <p className="text-sm text-gray-500">{step.desc}</p>
    </div>
  );
};

const TestimonialCard = ({ testimonial, index }) => {
  const [ref, isInView] = useInView();

  return (
    <div
      ref={ref}
      className={`border border-white/10 p-8 opacity-0 ${isInView ? "animate-slideUp" : ""}`}
      style={{ animationDelay: `${index * 0.15}s`, animationFillMode: "forwards" }}
    >
      {/* Quote */}
      <div className="font-display text-6xl text-flowkraft-primary/20 leading-none mb-4">"</div>
      <p className="text-gray-300 text-lg leading-relaxed mb-8">
        {testimonial.quote}
      </p>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-flowkraft-primary/20 flex items-center justify-center text-flowkraft-secondary font-display text-xl">
          {testimonial.author.charAt(0)}
        </div>
        <div>
          <div className="text-white font-medium">{testimonial.author}</div>
          <div className="text-sm text-gray-500">{testimonial.role}</div>
        </div>
      </div>
    </div>
  );
};

const ContactDetail = ({ icon, label, value, href }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-flowkraft-secondary">
      {icon}
    </div>
    <div>
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      {href ? (
        <a href={href} className="text-white hover:text-flowkraft-secondary transition-colors">
          {value}
        </a>
      ) : (
        <div className="text-white">{value}</div>
      )}
    </div>
  </div>
);

export default FlowKraftWebsite;
